/**
 * Detalle de Factura x Mayor — vista unificada de una factura.
 *
 * Desde una sola pantalla (diálogo a pantalla completa abierto desde el
 * listado) se puede:
 *   - Ver la cabecera y las líneas (chasis) de la factura, incluido el
 *     historial de chasis devueltos.
 *   - Ver el plan de pago (cuotas) con su estado.
 *   - Modificar el precio de los motores (ares9756) y guardar.
 *   - Devolver un motor de una factura consignada (queda como DEVUELTO).
 *   - Aplicar un cobro a las cuotas pendientes (ares9715) e imprimir recibo.
 *   - Imprimir el comprobante de la factura.
 *
 * Permisos por sección: precios ares9756, cobro ares9715, devolución
 * ares9720 (el backend re-valida todos).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SaveIcon from "@mui/icons-material/Save";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  fpInputSx,
  fpDataGridSx,
  TooltipRazonCancelacion,
  RazonCancelacionTexto,
} from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import {
  fmtMoney,
  monedaLabel,
  imprimirFactura,
  imprimirReciboCobroXmayor,
  etiquetaCuota,
  esCuotaInicial,
} from "modules/ventasxmayor/impresionxmayor";
import { fmtTasa, normalizaLegacy } from "lib/moneda";
// Motor de documentos: la impresión y la descarga son las mismas del módulo
// de plantillas, para que un contrato x mayor salga idéntico a uno de préstamo.
import { imprimirHtml, descargarB64 } from "modules/documentacion/documentos";

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Compara en centavos enteros: valor y valor_pagado vienen de columnas FLOAT,
// así que el pendiente podía quedar en 1073.3299… y el monto tecleado
// (1073.33) "excedía" por milésimas.
const centavos = (v) => Math.round(toNum(v) * 100);
const excede = (monto, pendiente) => centavos(monto) > centavos(pendiente);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

// Pestañas tipo píldora (mismo estilo del expediente: expedientedetalle.js).
// El tema de la plantilla fuerza color dark con !important en el Tab activo,
// por eso el color blanco lo devuelve también con !important.
const tabSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.85rem",
  minHeight: 40,
  px: 2,
  borderRadius: 999,
  border: `1px solid ${FP.color.border}`,
  color: FP.color.textSecondary,
  bgcolor: "#FFFFFF",
  "& .MuiSvgIcon-root": { color: "inherit" },
  "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
  "&.MuiTab-root.Mui-selected": {
    bgcolor: "#0B1220",
    borderColor: "#0B1220",
    color: "#FFFFFF !important",
  },
  "&.MuiTab-root.Mui-selected .MuiSvgIcon-root": {
    color: "#FFFFFF !important",
  },
};

function Dato({ label, value, strong = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "1.05rem" : "0.86rem",
          fontWeight: strong ? 800 : 600,
          color: FP.color.textPrimary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function FacturaDetalleXmayor({ facturaId, onExit, onChanged } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  const puedePrecios = tienePermisosModulo(roleDetalles, "ares9756");
  const puedePagar = tienePermisosModulo(roleDetalles, "ares9715");
  const puedeDevolver = tienePermisosModulo(roleDetalles, "ares9720");
  const puedeAnularCobro = tienePermisosModulo(roleDetalles, "ares9740");

  const [det, setDet] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState(0);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/xmayor_factura_detalle", { id: facturaId });
      if (ok) {
        setDet(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar la factura", color: "error" });
        if (onExit) onExit();
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (facturaId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaId]);

  const f = det?.factura || {};
  // Moneda de la factura: todos los importes de esta pantalla (líneas,
  // cuotas, pendientes) están en ella.
  const mon = monedaLabel(f.moneda);
  // Facturas en dólares: la tasa con la que se emitió va en la cabecera.
  const enDolar = normalizaLegacy(f.moneda) === "USD";
  // Inicial pactado (cuota 0 del plan): pactado, cobrado, pendiente,
  // vencimiento del completivo y los recibos con que se cobró.
  const cuotaInicial = (det?.cuotas || []).find((c) => esCuotaInicial(c.numero_pago));
  const inicialPactado = toNum(f.inicialpactado ?? cuotaInicial?.valor);
  const inicialPagado = toNum(f.inicialpagado ?? cuotaInicial?.valor_pagado);
  const inicialPendiente = Math.max(0, Math.round((inicialPactado - inicialPagado) * 100) / 100);
  const vencInicial = cuotaInicial?.fecha || "";
  const inicialVencido = (() => {
    if (inicialPendiente <= 0 || !vencInicial) return false;
    const [d, m, y] = vencInicial.split("/").map(Number);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return new Date(y, m - 1, d) < hoy;
  })();
  const recibosInicial = (det?.pagos || [])
    .filter((p) => esCuotaInicial(p.cuota) && !p.anulado)
    .map((p) => `#${p.recibo}`);
  const anulada = !!f.anulada;
  const consignada = !!f.consignada;

  // ---- Contrato (documento con plantilla) ----
  // Reusa el motor de documentos: las plantillas marcadas como "Venta x mayor"
  // se llenan con los datos de ESTA factura (cliente, cuotas y la tabla de
  // motores en {{listachasis}}). El permiso lo da la pantalla de facturas, no
  // el programa `documentos` (que es el editor de plantillas).
  const [contratoOpen, setContratoOpen] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState("");
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
  const [generando, setGenerando] = useState(""); // "" | "imprimir" | "word"

  const abrirContrato = async () => {
    setContratoOpen(true);
    setCargandoPlantillas(true);
    try {
      const { ok, data } = await post("/api/xmayor_contrato_plantillas", { id: facturaId });
      if (!ok) {
        showAlert({
          message: data.Error || "No se pudieron cargar las plantillas",
          color: "error",
        });
        return;
      }
      const lista = data.plantillas || [];
      setPlantillas(lista);
      // Con una sola plantilla no tiene sentido obligar a elegirla.
      setPlantillaSel(lista.length === 1 ? lista[0].id : "");
    } finally {
      setCargandoPlantillas(false);
    }
  };

  const generarContrato = async (modo) => {
    if (!plantillaSel) {
      showAlert({ message: "Seleccione una plantilla", color: "warning" });
      return;
    }
    setGenerando(modo);
    try {
      const { ok, data } = await post("/api/xmayor_contrato_generar", {
        id: plantillaSel,
        idfactura: facturaId,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo generar el contrato", color: "error" });
        return;
      }
      if (modo === "imprimir") {
        imprimirHtml(data.html || "", data.nombre);
      } else if (data.archivo_b64) {
        descargarB64(data.archivo_b64, data.nombre);
      } else {
        showAlert({ message: "No se pudo convertir a Word; use Imprimir / PDF", color: "warning" });
      }
      setContratoOpen(false);
    } finally {
      setGenerando("");
    }
  };

  // ---- Líneas (chasis): precio editable en las activas ----
  const [precios, setPrecios] = useState({});
  useEffect(() => {
    const ini = {};
    (det?.lineas || []).forEach((l) => {
      if (l.devuelto !== "S") ini[l.chasis] = String(toNum(l.preciomotor));
    });
    setPrecios(ini);
  }, [det]);

  const lineas = useMemo(() => (det?.lineas || []).map((l) => ({ ...l, id: l.chasis })), [det]);

  const totalVivo = useMemo(
    () =>
      lineas.reduce(
        (acc, l) => (l.devuelto === "S" ? acc : acc + toNum(precios[l.chasis] ?? l.preciomotor)),
        0
      ),
    [lineas, precios]
  );

  const preciosModificados = useMemo(
    () =>
      lineas.some((l) => l.devuelto !== "S" && toNum(precios[l.chasis]) !== toNum(l.preciomotor)),
    [lineas, precios]
  );

  // El precio no puede quedar por debajo del mínimo del chasis (producto.mprecio,
  // que el backend devuelve ya convertido a la moneda de la factura).
  const bajoElMinimo = (l) =>
    l.devuelto !== "S" &&
    toNum(l.preciominimo) > 0 &&
    toNum(precios[l.chasis] ?? l.preciomotor) < toNum(l.preciominimo);
  const hayBajoMinimo = useMemo(() => lineas.some(bajoElMinimo), [lineas, precios]);

  const [guardandoPrecios, setGuardandoPrecios] = useState(false);

  const avisarResultado = (data, prefijo) => {
    let msg = `${prefijo}. Nuevo total ${mon} ${fmtMoney(data.total)}`;
    if (toNum(data.cuotasRecalculadas) > 0) {
      msg += `. Se recalcularon ${data.cuotasRecalculadas} cuotas`;
    }
    if (data.cerrada) msg += ". La factura quedó saldada";
    showAlert({ message: msg, color: "success" });
  };

  const guardarPrecios = async () => {
    const items = lineas
      .filter((l) => l.devuelto !== "S")
      .map((l) => ({ chasis: l.chasis, precio: toNum(precios[l.chasis]) }));
    if (items.some((i) => i.precio < 0)) {
      showAlert({ message: "El precio de un motor no puede ser negativo", color: "warning" });
      return;
    }
    const bajoMinimo = lineas.find(bajoElMinimo);
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al precio mínimo (${monedaLabel(f.moneda)} ${fmtMoney(
          bajoMinimo.preciominimo
        )})`,
        color: "warning",
      });
      return;
    }
    setGuardandoPrecios(true);
    try {
      const { ok, data } = await post("/api/xmayor_precios_guardar", { id: f.id, items });
      if (ok) {
        avisarResultado(data, data.ok || "Precios guardados");
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los precios", color: "error" });
      }
    } finally {
      setGuardandoPrecios(false);
    }
  };

  // ---- Devolución de chasis (consignadas) ----
  const [devChasis, setDevChasis] = useState(null);
  const [devolviendo, setDevolviendo] = useState(false);

  const devolver = async () => {
    setDevolviendo(true);
    try {
      const { ok, data } = await post("/api/xmayor_chasis_devolver", {
        id: f.id,
        chasis: devChasis.chasis,
      });
      if (ok) {
        showAlert({ message: data.ok || "Chasis devuelto", color: "success" });
        setDevChasis(null);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo devolver el chasis", color: "error" });
      }
    } finally {
      setDevolviendo(false);
    }
  };

  // ---- Plan de pago + cobro ----
  const [apagar, setApagar] = useState({});
  const [montoRecibido, setMontoRecibido] = useState("");
  useEffect(() => {
    setApagar({});
    setMontoRecibido("");
  }, [det]);

  const cuotas = useMemo(
    () =>
      (det?.cuotas || []).map((c) => ({
        ...c,
        pendiente: Math.max(0, centavos(c.valor) - centavos(c.valor_pagado)) / 100,
      })),
    [det]
  );

  const totalPendiente = useMemo(() => cuotas.reduce((acc, c) => acc + c.pendiente, 0), [cuotas]);

  const totalAPagar = useMemo(
    () => cuotas.reduce((acc, c) => acc + toNum(apagar[c.id] || 0), 0),
    [cuotas, apagar]
  );

  // Reparte el monto recibido entre las cuotas pendientes, de la más vieja a
  // la más nueva (mismo criterio del cobro de préstamos: numero_pago asc).
  const distribuir = () => {
    const monto = toNum(montoRecibido);
    if (monto <= 0) {
      showAlert({ message: "Debe indicar el monto recibido para distribuir", color: "warning" });
      return;
    }
    let restante = monto;
    const asignado = {};
    cuotas.forEach((c) => {
      if (c.pendiente <= 0.01 || restante <= 0) return;
      const pago = Math.min(restante, c.pendiente);
      asignado[c.id] = String(Math.round(pago * 100) / 100);
      restante = Math.round((restante - pago) * 100) / 100;
    });
    setApagar(asignado);
    if (restante > 0.01) {
      showAlert({
        message: `El monto excede el pendiente de la factura: se distribuyeron ${mon} ${fmtMoney(
          monto - restante
        )} y sobran ${mon} ${fmtMoney(restante)}`,
        color: "warning",
      });
    }
  };

  const validarAPagar = (row) => {
    if (excede(apagar[row.id], row.pendiente)) {
      showAlert({
        message: `El valor excede el pendiente de la cuota ${etiquetaCuota(
          row.numero_pago
        )} (${mon} ${fmtMoney(row.pendiente)})`,
        color: "warning",
      });
      setApagar((prev) => ({ ...prev, [row.id]: "0" }));
    }
  };

  const [tasa, setTasa] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tipodepago, setTipodepago] = useState("Contado");
  const [referenciapago, setReferenciapago] = useState("");
  const [pagando, setPagando] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);

  const cuotasPendientes = useMemo(() => cuotas.filter((c) => c.pendiente > 0.01), [cuotas]);

  // ---- Historial de pagos ----
  const pagosHistorial = useMemo(
    () => (det?.pagos || []).map((p, i) => ({ ...p, id: `${p.recibo}-${p.cuota}-${i}` })),
    [det]
  );

  const [reimprimiendo, setReimprimiendo] = useState(0);

  const reimprimirRecibo = async (recibo) => {
    setReimprimiendo(recibo);
    try {
      const { ok, data } = await post("/api/xmayor_recibo", { recibo });
      if (ok) {
        imprimirReciboCobroXmayor(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el recibo", color: "error" });
      }
    } finally {
      setReimprimiendo(0);
    }
  };

  // Anulación de un cobro: revierte el recibo COMPLETO (todas sus cuotas,
  // incluso las de otras facturas si el recibo fue multi-factura).
  const [anularRecibo, setAnularRecibo] = useState(null);
  const [anulandoCobro, setAnulandoCobro] = useState(false);

  const anularCobro = async () => {
    setAnulandoCobro(true);
    try {
      const { ok, data } = await post("/api/xmayor_cobro_anular", { recibo: anularRecibo.recibo });
      if (ok) {
        showAlert({ message: data.ok || "Cobro anulado", color: "success" });
        setAnularRecibo(null);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo anular el cobro", color: "error" });
      }
    } finally {
      setAnulandoCobro(false);
    }
  };

  const abrirPago = () => {
    setApagar({});
    setMontoRecibido("");
    setTasa("");
    setDescripcion("");
    setTipodepago("Contado");
    setReferenciapago("");
    setPagoOpen(true);
  };

  const pagar = async () => {
    const detalle = cuotas
      .filter((c) => toNum(apagar[c.id]) > 0)
      .map((c) => ({ id: c.id, valor: toNum(apagar[c.id]) }));
    if (detalle.length === 0) {
      showAlert({
        message: "Debe indicar el valor a pagar de al menos una cuota",
        color: "warning",
      });
      return;
    }
    const excedida = cuotas.find((c) => excede(apagar[c.id], c.pendiente));
    if (excedida) {
      showAlert({
        message: `El valor excede el pendiente de la cuota ${etiquetaCuota(excedida.numero_pago)}`,
        color: "warning",
      });
      return;
    }
    if (toNum(tasa) <= 0) {
      showAlert({ message: "Debe indicar la tasa del día", color: "warning" });
      return;
    }
    if (!descripcion.trim()) {
      showAlert({ message: "La descripción del cobro no puede estar en blanco", color: "warning" });
      return;
    }
    setPagando(true);
    try {
      const { ok, data } = await post("/api/xmayor_pagar", {
        cliente: f.suplidor,
        tasa: toNum(tasa),
        descripcion: descripcion.trim(),
        tipodepago,
        referenciapago: referenciapago.trim(),
        detalle,
      });
      if (ok) {
        showAlert({ message: data.ok || "Cobro registrado", color: "success" });
        const recibo = await post("/api/xmayor_recibo", { recibo: data.recibo });
        if (recibo.ok) {
          imprimirReciboCobroXmayor(recibo.data);
        } else {
          showAlert({
            message: recibo.data.Error || "No se pudo cargar el recibo para imprimir",
            color: "error",
          });
        }
        setPagoOpen(false);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el cobro", color: "error" });
      }
    } finally {
      setPagando(false);
    }
  };

  // ---- Presentación ----
  const estadoChip = () => {
    if (anulada) {
      if (toNum(f.devueltos) > 0) {
        return (
          <Chip
            size="small"
            label="Devuelta"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        );
      }
      return (
        <TooltipRazonCancelacion razon={f.razonanula}>
          <Chip
            size="small"
            label="Anulada"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        </TooltipRazonCancelacion>
      );
    }
    if (f.status === "C") {
      return (
        <Chip
          size="small"
          label="Saldada"
          sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Vigente"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  };

  const chipLinea = (l) => {
    if (l.devuelto === "S") {
      return (
        <Chip
          size="small"
          label={`Devuelto ${l.fechadevuelto || ""}`.trim()}
          sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
        />
      );
    }
    if (l.chasispagado === "S") {
      return (
        <Chip
          size="small"
          label="Saldado"
          sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
        />
      );
    }
    if (consignada) {
      return (
        <Chip
          size="small"
          label="Consignado"
          sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Facturado"
        sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
      />
    );
  };

  const columnasLineas = [
    { field: "chasis", headerName: "Chasis", width: 170 },
    { field: "marca", headerName: "Marca", width: 110 },
    { field: "modelo", headerName: "Modelo", width: 110 },
    { field: "colore", headerName: "Color", width: 90 },
    {
      field: "preciomotor",
      headerName: "Precio",
      width: 140,
      sortable: false,
      filterable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const l = params.row;
        if (l.devuelto === "S" || !puedePrecios || anulada) {
          return (
            <Typography
              sx={{
                fontSize: "0.84rem",
                fontWeight: 600,
                textDecoration: l.devuelto === "S" ? "line-through" : "none",
                color: l.devuelto === "S" ? FP.color.textMuted : FP.color.textPrimary,
              }}
            >
              {fmtMoney(l.preciomotor)}
            </Typography>
          );
        }
        return (
          <TextField
            size="small"
            value={precios[l.chasis] ?? ""}
            onChange={(e) => setPrecios((prev) => ({ ...prev, [l.chasis]: e.target.value }))}
            error={bajoElMinimo(l)}
            title={toNum(l.preciominimo) > 0 ? `Precio mínimo ${fmtMoney(l.preciominimo)}` : ""}
            inputProps={{ style: { textAlign: "right" } }}
            sx={fpInputSx}
          />
        );
      },
    },
    {
      field: "preciominimo",
      headerName: "Mínimo",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => (toNum(value) > 0 ? fmtMoney(value) : "—"),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => chipLinea(params.row),
    },
    {
      field: "accion",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const l = params.row;
        if (!consignada || !puedeDevolver) return null;
        return (
          <Button
            size="small"
            startIcon={<AssignmentReturnOutlinedIcon />}
            onClick={() => setDevChasis(l)}
            disabled={anulada || l.devuelto === "S" || l.chasispagado === "S"}
            sx={btnOutline("#E65100")}
          >
            Devolver
          </Button>
        );
      },
    },
  ];

  const chipCuota = (c) => {
    if (c.status === "C" || c.pendiente <= 0.01) {
      return (
        <Chip
          size="small"
          label="Saldada"
          sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Pendiente"
        sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
      />
    );
  };

  const columnasCuotas = [
    {
      field: "numero_pago",
      headerName: "Cuota#",
      width: 90,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "fecha", headerName: "Fecha", width: 120 },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "valor_pagado",
      headerName: "Pagado",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "status",
      headerName: "Estado",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => chipCuota(params.row),
    },
  ];

  // Columnas del diálogo de pago (solo cuotas pendientes, con "A pagar").
  const columnasCuotasPago = [
    {
      field: "numero_pago",
      headerName: "Cuota#",
      width: 80,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "apagar",
      headerName: "A pagar",
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={apagar[params.row.id] ?? ""}
          placeholder="0.00"
          onChange={(e) => setApagar((prev) => ({ ...prev, [params.row.id]: e.target.value }))}
          onBlur={() => validarAPagar(params.row)}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
  ];

  // Columnas del historial de pagos (detalle por cuota de cada recibo).
  const columnasPagos = [
    { field: "recibo", headerName: "Recibo#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "cuota",
      headerName: "Cuota#",
      width: 80,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "banco", headerName: "Tipo pago", width: 120 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 150 },
    { field: "usuario", headerName: "Usuario", width: 110 },
    {
      field: "estado",
      headerName: "Estado",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.anulado ? (
          <Chip
            size="small"
            label="Anulado"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Aplicado"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    ...(puedePagar || puedeAnularCobro
      ? [
          {
            field: "accion",
            headerName: "",
            width: 230,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.8}>
                {puedePagar && (
                  <Button
                    size="small"
                    startIcon={
                      reimprimiendo === params.row.recibo ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <PrintOutlinedIcon />
                      )
                    }
                    onClick={() => reimprimirRecibo(params.row.recibo)}
                    disabled={params.row.anulado || reimprimiendo === params.row.recibo}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Recibo
                  </Button>
                )}
                {puedeAnularCobro && (
                  <Button
                    size="small"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setAnularRecibo(params.row)}
                    disabled={params.row.anulado}
                    sx={btnOutline(FP.color.danger)}
                  >
                    Anular
                  </Button>
                )}
              </Stack>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {/* Barra superior */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
            Volver
          </Button>
          <Typography sx={{ fontWeight: 800, fontSize: "1.02rem", color: FP.color.textPrimary }}>
            Factura x Mayor #{facturaId}
          </Typography>
          {det && estadoChip()}
          {det && anulada && <RazonCancelacionTexto razon={f.razonanula} />}
          {det && consignada && (
            <Chip
              size="small"
              label="Consignada"
              sx={{ bgcolor: "#EDE7F6", color: "#4527A0", fontWeight: 700 }}
            />
          )}
          {/* Facturas nacidas de un conduce: no salen en el listado de
              facturas, así que aquí se dice de dónde vienen. */}
          {det && f.conduce ? (
            <Chip
              size="small"
              label={
                (f.conduces || []).length > 1
                  ? `Conduces ${f.conduces.map((x) => `#${x}`).join(", ")}`
                  : `Conduce #${f.conduce}`
              }
              sx={{ bgcolor: "#FFF8E1", color: "#E65100", fontWeight: 700 }}
            />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Button
            startIcon={<DescriptionOutlinedIcon />}
            onClick={abrirContrato}
            disabled={!det}
            sx={btnOutline(FP.color.navy)}
          >
            Contrato
          </Button>
          <Button
            startIcon={<PrintOutlinedIcon />}
            onClick={() => det && imprimirFactura(det)}
            disabled={!det}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      </Box>

      {cargando || !det ? (
        <Stack alignItems="center" sx={{ py: 10 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Cabecera */}
          <Box sx={sectionBox}>
            <Grid container spacing={2.4}>
              <Grid item xs={6} sm={4} md={enDolar ? 2.4 : 3}>
                <Dato label="CLIENTE" value={f.cliente || "—"} />
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <Dato label="FECHA" value={f.fecha || "—"} />
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <Dato label="TIPO" value={f.tipofactura || "—"} />
              </Grid>
              <Grid item xs={6} sm={2} md={enDolar ? 1.6 : 2}>
                <Dato label="NCF" value={f.ncf || "—"} />
              </Grid>
              {enDolar ? (
                <Grid item xs={6} sm={2} md={1}>
                  <Dato label="TASA" value={toNum(f.tasa) > 0 ? fmtTasa(f.tasa) : "—"} />
                </Grid>
              ) : null}
              <Grid item xs={4} sm={2} md={1.3}>
                <Dato label={`VALOR ${mon}`} value={fmtMoney(f.valor)} strong />
              </Grid>
              <Grid item xs={4} sm={2} md={1.3}>
                <Dato label="PAGADO" value={fmtMoney(f.vplicado)} strong />
              </Grid>
              <Grid item xs={4} sm={2} md={1.4}>
                <Dato label="BALANCE" value={fmtMoney(f.balance)} strong />
              </Grid>
              {f.concepto ? (
                <Grid item xs={12}>
                  <Dato label="CONCEPTO" value={f.concepto} />
                </Grid>
              ) : null}
            </Grid>
            {inicialPactado > 0 ? (
              <Box
                sx={{
                  mt: 2.4,
                  pt: 2,
                  borderTop: `1px dashed ${FP.color.border}`,
                }}
              >
                <Grid container spacing={2.4} alignItems="flex-end">
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato
                      label={`INICIAL PACTADO ${mon}`}
                      value={fmtMoney(inicialPactado)}
                      strong
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato label="INICIAL COBRADO" value={fmtMoney(inicialPagado)} strong />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato label="INICIAL PENDIENTE" value={fmtMoney(inicialPendiente)} strong />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato
                      label="VENCE EL COMPLETIVO"
                      value={inicialPendiente > 0 ? vencInicial || "—" : "Saldado"}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato
                      label="FINANCIADO EN CUOTAS"
                      value={fmtMoney(Math.max(0, toNum(f.valor) - inicialPactado))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Dato
                      label="RECIBOS DEL INICIAL"
                      value={recibosInicial.length ? recibosInicial.join(", ") : "—"}
                    />
                  </Grid>
                  {inicialVencido ? (
                    <Grid item xs={12}>
                      <Chip
                        size="small"
                        label={`Completivo vencido desde el ${vencInicial}`}
                        sx={{ bgcolor: "#FDECEA", color: FP.color.danger, fontWeight: 700 }}
                      />
                    </Grid>
                  ) : null}
                </Grid>
              </Box>
            ) : null}
          </Box>

          {/* Pestañas: Chasis / Plan de pago / Historial de pagos */}
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons={false}
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                mb: 2.4,
                minHeight: 44,
                "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
                "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
              }}
            >
              <Tab
                icon={<TwoWheelerOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Chasis"
                sx={tabSx}
              />
              <Tab
                icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Plan de pago"
                sx={tabSx}
              />
              <Tab
                icon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Historial de pagos (${pagosHistorial.length})`}
                sx={tabSx}
              />
            </Tabs>

            {/* Pestaña Chasis */}
            {tab === 0 && (
              <>
                <Box sx={{ bgcolor: "#fff" }}>
                  <DataGrid
                    autoHeight
                    rows={lineas}
                    columns={columnasLineas}
                    rowHeight={52}
                    hideFooter
                    sx={fpDataGridSx}
                    disableRowSelectionOnClick
                  />
                </Box>
                {puedePrecios && !anulada && (
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ mt: 2 }}
                  >
                    {hayBajoMinimo && (
                      <Typography
                        sx={{ fontWeight: 700, color: FP.color.danger, fontSize: "0.86rem" }}
                      >
                        Hay precios por debajo del mínimo del chasis
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.86rem" }}
                    >
                      Total con precios en pantalla: {mon} {fmtMoney(totalVivo)}
                    </Typography>
                    <Button
                      startIcon={
                        guardandoPrecios ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={guardarPrecios}
                      disabled={guardandoPrecios || !preciosModificados || hayBajoMinimo}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Guardar precios
                    </Button>
                  </Stack>
                )}
              </>
            )}

            {/* Pestaña Plan de pago */}
            {tab === 1 && (
              <>
                {puedePagar && !anulada && cuotasPendientes.length > 0 && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <Button
                      startIcon={<PaymentsOutlinedIcon />}
                      onClick={abrirPago}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Aplicar pago
                    </Button>
                  </Stack>
                )}
                {cuotas.length === 0 ? (
                  <Typography
                    sx={{ color: FP.color.textMuted, fontWeight: 600, fontSize: "0.88rem" }}
                  >
                    La factura no tiene plan de cuotas (contado o sin compromisos registrados).
                  </Typography>
                ) : (
                  <Box sx={{ bgcolor: "#fff" }}>
                    <DataGrid
                      autoHeight
                      rows={cuotas}
                      columns={columnasCuotas}
                      rowHeight={52}
                      hideFooter={cuotas.length <= 25}
                      sx={fpDataGridSx}
                      disableRowSelectionOnClick
                    />
                  </Box>
                )}
              </>
            )}

            {/* Pestaña Historial de pagos */}
            {tab === 2 &&
              (pagosHistorial.length === 0 ? (
                <Typography
                  sx={{ color: FP.color.textMuted, fontWeight: 600, fontSize: "0.88rem" }}
                >
                  La factura no tiene pagos registrados.
                </Typography>
              ) : (
                <Box sx={{ bgcolor: "#fff" }}>
                  <DataGrid
                    autoHeight
                    rows={pagosHistorial}
                    columns={columnasPagos}
                    rowHeight={46}
                    hideFooter={pagosHistorial.length <= 25}
                    sx={fpDataGridSx}
                    disableRowSelectionOnClick
                  />
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* Diálogo de registro de pago */}
      <Dialog
        open={pagoOpen}
        onClose={() => !pagando && setPagoOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Aplicar pago — Factura {f.id} ({f.cliente})
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 1.5, mt: 0 }}>
            <Grid item xs={7} sm={4}>
              <TextField
                fullWidth
                label="Monto recibido"
                value={montoRecibido}
                onChange={(e) => setMontoRecibido(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") distribuir();
                }}
                placeholder="0.00"
                helperText={`Pendiente: ${mon} ${fmtMoney(totalPendiente)}`}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={5} sm={3} sx={{ pt: { sm: 0.4 } }}>
              <Button
                fullWidth
                startIcon={<CallSplitOutlinedIcon />}
                onClick={distribuir}
                sx={{ ...btnOutline(FP.color.success), height: 44 }}
              >
                Distribuir
              </Button>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Total a pagar"
                value={fmtMoney(totalAPagar)}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff", mb: 2 }}>
            <DataGrid
              autoHeight
              rows={cuotasPendientes}
              columns={columnasCuotasPago}
              rowHeight={52}
              hideFooter
              sx={fpDataGridSx}
              disableRowSelectionOnClick
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Tipo de pago"
                value={tipodepago}
                onChange={(e) => setTipodepago(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencias">Transferencias</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Referencia"
                value={referenciapago}
                onChange={(e) => setReferenciapago(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPagoOpen(false)} disabled={pagando} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={pagando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={pagar}
            disabled={pagando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Registrar cobro e imprimir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de anulación de cobro */}
      <Dialog
        open={!!anularRecibo}
        onClose={() => !anulandoCobro && setAnularRecibo(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular cobro</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Anular el recibo <b>#{anularRecibo?.recibo}</b> del <b>{anularRecibo?.fecha || "—"}</b>.
            Se revierte el recibo COMPLETO: todas sus cuotas vuelven a pendiente y el balance de la
            factura aumenta (si el recibo cubrió otras facturas del cliente, también se revierten).
            ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAnularRecibo(null)} disabled={anulandoCobro} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulandoCobro ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anularCobro}
            disabled={anulandoCobro}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de devolución */}
      <Dialog
        open={!!devChasis}
        onClose={() => setDevChasis(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#E65100" }}>Confirmar devolución</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Devolver el chasis <b>{devChasis?.chasis}</b> ({devChasis?.marca} {devChasis?.modelo})
            por {mon} <b>{fmtMoney(devChasis?.preciomotor)}</b>. El vehículo vuelve a disponible y
            la línea queda como DEVUELTO en el historial de la factura. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDevChasis(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              devolviendo ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AssignmentReturnOutlinedIcon />
              )
            }
            onClick={devolver}
            disabled={devolviendo}
            sx={btnSolid("#E65100", "#BF360C")}
          >
            Sí, devolver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contrato: se elige la plantilla y sale por impresión o como Word. */}
      <Dialog
        open={contratoOpen}
        onClose={() => !generando && setContratoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Contrato de la factura #{facturaId}
        </DialogTitle>
        <DialogContent>
          {cargandoPlantillas && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {!cargandoPlantillas && plantillas.length === 0 && (
            <DialogContentText sx={{ fontSize: "0.9rem" }}>
              No hay plantillas de venta x mayor. Cree una en Configuración → Documentos, eligiendo
              &quot;Venta x mayor&quot; como origen de los datos.
            </DialogContentText>
          )}
          {!cargandoPlantillas && plantillas.length > 0 && (
            <TextField
              select
              fullWidth
              label="Plantilla"
              value={plantillaSel}
              onChange={(e) => setPlantillaSel(e.target.value)}
              sx={{ ...fpInputSx, mt: 1 }}
            >
              {plantillas.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                  {p.descripcion ? ` — ${p.descripcion}` : ""}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setContratoOpen(false)}
            disabled={!!generando}
            sx={btnOutline(FP.color.navy)}
          >
            Cerrar
          </Button>
          <Button
            startIcon={
              generando === "word" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadOutlinedIcon />
              )
            }
            onClick={() => generarContrato("word")}
            disabled={!!generando || !plantillaSel}
            sx={btnOutline(FP.color.navy)}
          >
            Word
          </Button>
          <Button
            startIcon={
              generando === "imprimir" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            onClick={() => generarContrato("imprimir")}
            disabled={!!generando || !plantillaSel}
            sx={btnSolid(FP.color.navy, FP.color.navy)}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
